import { useCallback, useEffect, useRef, useState } from "react";
import {
  DeviceMobile, DownloadSimple, CheckCircle, CircleNotch, ArrowSquareOut,
  ShareNetwork, PlusSquare, DotsThreeVertical, ShieldCheck, ArrowsClockwise, CopySimple,
} from "@phosphor-icons/react";

// Chrome versions older than this are treated as OUTDATED (update first) rather
// than capability-limited. Android 11 itself is fully supported — only the
// installed Chrome build governs beforeinstallprompt availability.
const MIN_CHROME_MAJOR = 120;
const CHROME_PLAY_URL = "https://play.google.com/store/apps/details?id=com.android.chrome";

const ua = () => navigator.userAgent;
const isIOS = () =>
  /iPad|iPhone|iPod/.test(ua()) || (ua().includes("Mac") && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/i.test(ua());
const chromeMajor = () => {
  const m = ua().match(/Chrome\/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
};
const androidVersion = () => {
  const m = ua().match(/Android\s([\d.]+)/);
  return m ? m[1] : null;
};
export const browserName = () => {
  const u = ua();
  if (/FxiOS|Firefox\//.test(u)) return "Firefox";
  if (/; wv\)/.test(u)) return "an in-app browser";
  if (/SamsungBrowser\//.test(u)) return "Samsung Internet";
  if (/EdgA\//.test(u)) return "Microsoft Edge";
  if (/OPR\//.test(u)) return "Opera";
  if (/Chrome\//.test(u)) return "Chrome";
  return "an unsupported browser";
};
// Android browsers without a Chromium engine token (no "Chrome/" in the UA,
// e.g. Firefox) never implement beforeinstallprompt — installation is impossible
// there and the user must move to Chrome.
export const isNonChromiumAndroid = () => isAndroid() && chromeMajor() === null;

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3 text-left">
      <span className="shrink-0 h-6 w-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">{n}</span>
      <span className="t-support text-muted-foreground">{children}</span>
    </li>
  );
}

// Local-only installation diagnostics (never transmitted automatically).
async function gatherDiagnostics() {
  const lines = [];
  try {
    const res = await fetch("/manifest.webmanifest", { cache: "no-store" });
    const m = await res.json();
    const pngIcons = (m.icons || []).filter((i) => i.type === "image/png").length;
    lines.push(`manifest: ${res.ok ? "OK" : `HTTP ${res.status}`} — display=${m.display}, start_url=${m.start_url}, scope=${m.scope}, png_icons=${pngIcons}`);
    for (const icon of m.icons || []) {
      try {
        const ir = await fetch(icon.src, { cache: "no-store" });
        lines.push(`icon ${icon.sizes}: ${ir.ok ? "OK" : `HTTP ${ir.status}`}`);
      } catch (_) {
        lines.push(`icon ${icon.sizes}: FETCH FAILED`);
      }
    }
  } catch (_) {
    lines.push("manifest: FAILED to load/parse");
  }
  try {
    const reg = await navigator.serviceWorker?.getRegistration?.();
    lines.push(`service_worker: registered=${!!reg}, controlling=${!!navigator.serviceWorker?.controller}`);
  } catch (_) {
    lines.push("service_worker: unavailable");
  }
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  lines.push(`display_mode: ${standalone ? "standalone" : "browser"}`);
  lines.push(`install_prompt_event: ${window.__bipEvent ? "captured" : "not-received"}; appinstalled=${window.__pwaInstalled ? "fired" : "not-fired"}`);
  lines.push(`secure_context: ${window.isSecureContext}`);
  lines.push(`android_version: ${androidVersion() ?? "n/a"}; chrome_version: ${chromeMajor() ?? "n/a"}`);
  lines.push(`browser: ${browserName()}; chromium_engine: ${/Chrome\//.test(ua()) ? "yes" : "NO — install prompt unavailable in this browser"}`);
  lines.push(`user_agent: ${ua()}`);
  lines.push(`checked_at: ${new Date().toISOString()}`);
  return lines.join("\n");
}

// Blocking post-PIN step: install the Rotary GOV Report Builder on the home screen.
// Android Chrome: invokes the saved native beforeinstallprompt immediately.
// Android with OUTDATED Chrome (no prompt): "Update Chrome" first, with automatic
//   recheck when the user returns; browser access only as verified last resort.
// iOS/iPadOS: guided Add to Home Screen route only.
export function InstallStep() {
  const ios = isIOS();
  const [installed, setInstalled] = useState(() => !!window.__pwaInstalled);
  const [canPrompt, setCanPrompt] = useState(() => !!window.__bipEvent);
  const [prompting, setPrompting] = useState(false);
  // null | "update" (outdated Chrome) | "fallback" (mandatory guided manual install)
  const [noPromptPhase, setNoPromptPhase] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [copied, setCopied] = useState(false);
  const promptingRef = useRef(false);

  const showHelp = useCallback(async () => {
    setDiagnostics(await gatherDiagnostics());
  }, []);

  const copyDiagnostics = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(diagnostics || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* user can select the text manually */ }
  }, [diagnostics]);

  const triggerPrompt = useCallback(async () => {
    const ev = window.__bipEvent;
    if (!ev || promptingRef.current) return;
    promptingRef.current = true;
    setPrompting(true);
    setNoPromptPhase(null);
    try {
      ev.prompt();
      const choice = await ev.userChoice;
      window.__bipEvent = null; // a captured event can only prompt once
      if (choice?.outcome !== "accepted") {
        setCanPrompt(false);
        setNoPromptPhase("fallback");
      }
    } catch (_) {
      window.__bipEvent = null;
      setCanPrompt(false);
      setNoPromptPhase("fallback");
    }
    promptingRef.current = false;
    setPrompting(false);
  }, []);

  // Local-only diagnostics for troubleshooting (never sent to analytics/audit).
  useEffect(() => {
    if (!isAndroid()) return;
    try {
      localStorage.setItem("rp_install_diag", JSON.stringify({
        android: androidVersion(), chrome: chromeMajor(),
        checked_at: new Date().toISOString(),
      }));
    } catch (_) { /* diagnostics only */ }
  }, []);

  // Automatic recheck: if the install prompt becomes available at any time
  // (including after the user updates Chrome and returns), invoke it right away.
  useEffect(() => {
    const onReady = () => { setCanPrompt(true); triggerPrompt(); };
    const onInstalled = () => setInstalled(true);
    const onVisible = () => {
      if (document.visibilityState === "visible" && window.__bipEvent) onReady();
    };
    window.addEventListener("pwa-install-ready", onReady);
    window.addEventListener("pwa-installed", onInstalled);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.removeEventListener("pwa-install-ready", onReady);
      window.removeEventListener("pwa-installed", onInstalled);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [triggerPrompt]);

  // On entry: fire the native dialog right away on Android Chrome. If no prompt
  // arrives within the grace period, verify genuine installability (secure
  // context + active service worker), then route by Chrome version:
  // outdated Chrome -> "Update Chrome" first; current Chrome -> last-resort path.
  useEffect(() => {
    if (ios || installed) return undefined;
    if (isNonChromiumAndroid()) {
      // beforeinstallprompt can never arrive here — route to Chrome immediately.
      setNoPromptPhase("browser");
      return undefined;
    }
    if (window.__bipEvent) {
      triggerPrompt();
      return undefined;
    }
    const t = setTimeout(async () => {
      if (window.__bipEvent || window.__pwaInstalled) return;
      let reg = null;
      try { reg = await navigator.serviceWorker?.getRegistration?.(); } catch (_) { /* stays waiting */ }
      if (!window.isSecureContext || !reg) return;
      const cm = chromeMajor();
      if (isAndroid() && cm !== null && cm < MIN_CHROME_MAJOR) setNoPromptPhase("update");
      else setNoPromptPhase("fallback");
    }, 4000);
    return () => clearTimeout(t);
  }, [ios, installed, triggerPrompt]);

  if (installed) {
    return (
      <div data-testid="pwa-installed-confirmation" className="text-center">
        <CheckCircle size={44} weight="duotone" className="text-green-600 mx-auto mt-5" />
        <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="pwa-installed-title">
          Rotary GOV Report Builder installed
        </h1>
        <p className="mt-3 t-support text-muted-foreground text-left">
          The app has been added to your home screen. You can close this browser tab.
        </p>
        <p className="mt-2 t-support text-muted-foreground text-left">
          From now on, open the <b>Rotary GOV Report Builder</b> icon on your home screen. It launches
          full-screen — no address bar — with a branded splash, then asks for your six-digit
          Rotary Access PIN.
        </p>
      </div>
    );
  }

  if (!ios && noPromptPhase === "browser" && !canPrompt && !prompting) {
    return (
      <div data-testid="wrong-browser-step" className="text-center">
        <ArrowSquareOut size={40} weight="duotone" className="text-primary mx-auto mt-5" />
        <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="wrong-browser-title">
          Open this setup in Chrome
        </h1>
        <p className="mt-3 t-support text-muted-foreground text-left">
          Your Rotary Access PIN is set, but this page is running in <b>{browserName()}</b>,
          which cannot install the Rotary GOV Report Builder app. On Android, installation
          requires <b>Google Chrome</b>.
        </p>
        <ol data-testid="wrong-browser-instructions" className="mt-5 space-y-3">
          <Step n={1}>
            Open <b>Chrome</b> on this phone (install it free from the Play Store if needed).
          </Step>
          <Step n={2}>
            On your signed-in computer, open <b>Get Mobile Access</b> and generate a <b>new</b> QR code
            — each code works only once.
          </Step>
          <Step n={3}>
            Scan it from inside Chrome: tap Chrome&apos;s address bar and choose the scan
            icon <ArrowsClockwise size={15} weight="bold" className="inline text-primary" />, or set
            Chrome as your default browser and scan with your camera.
          </Step>
          <Step n={4}>
            Complete the PIN step in Chrome — the installation prompt then appears automatically.
          </Step>
        </ol>
        <a
          data-testid="get-chrome-button" href={CHROME_PLAY_URL} target="_blank" rel="noopener noreferrer"
          className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <ArrowSquareOut size={18} weight="bold" />
          Get Chrome from the Play Store
        </a>
        {!diagnostics ? (
          <button
            data-testid="install-help-toggle" type="button" onClick={showHelp}
            className="mt-4 text-sm text-primary hover:underline"
          >
            Installation help
          </button>
        ) : (
          <div data-testid="install-diagnostics" className="mt-4 text-left">
            <pre className="mt-2 p-3 bg-background border border-border rounded-md text-[11px] leading-relaxed whitespace-pre-wrap break-words select-all">{diagnostics}</pre>
            <button
              data-testid="copy-diagnostics-button" type="button" onClick={copyDiagnostics}
              className="mt-2 inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <CopySimple size={15} weight="bold" /> {copied ? "Copied" : "Copy diagnostics"}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!ios && noPromptPhase === "update" && !canPrompt && !prompting) {
    return (
      <div data-testid="chrome-update-step" className="text-center">
        <ArrowsClockwise size={40} weight="duotone" className="text-primary mx-auto mt-5" />
        <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="chrome-update-title">
          Update Chrome to continue installation
        </h1>
        <p className="mt-3 t-support text-muted-foreground text-left">
          Your Rotary Access PIN is set. This phone is fully supported — only its Chrome
          browser is too old to show the installation prompt. Update Chrome from the Play
          Store, then return here.
        </p>
        <a
          data-testid="chrome-update-button" href={CHROME_PLAY_URL} target="_blank" rel="noopener noreferrer"
          className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <ArrowSquareOut size={18} weight="bold" />
          Update Chrome
        </a>
        <p className="mt-3 t-support text-muted-foreground">
          After updating, return to this screen — installation continues automatically.
        </p>
        <button
          data-testid="chrome-cannot-update-link" type="button" onClick={() => setNoPromptPhase("fallback")}
          className="mt-5 text-sm text-primary hover:underline"
        >
          Chrome can&apos;t be updated on this phone?
        </button>
      </div>
    );
  }

  return (
    <div data-testid="pwa-install-step" className="text-center">
      <DeviceMobile size={40} weight="duotone" className="text-primary mx-auto mt-5" />
      <h1 className="mt-4 font-display font-extrabold tracking-tighter text-2xl" data-testid="pwa-install-title">
        One final step
      </h1>
      <p className="mt-3 t-support text-muted-foreground text-left">
        Your Rotary Access PIN is set. To finish, install the Rotary GOV Report Builder on this
        phone&apos;s home screen. The installed app opens full-screen and unlocks with your PIN.
      </p>

      {ios ? (
        <ol data-testid="pwa-ios-instructions" className="mt-5 space-y-3">
          <Step n={1}>
            Tap the <b>Share</b> button <ShareNetwork size={15} weight="bold" className="inline text-primary" /> in
            Safari&apos;s toolbar.
          </Step>
          <Step n={2}>
            Scroll and tap <b>Add to Home Screen</b> <PlusSquare size={15} weight="bold" className="inline text-primary" />.
          </Step>
          <Step n={3}>
            Tap <b>Add</b>, then open <b>Rotary GOV Report Builder</b> from your home screen.
          </Step>
        </ol>
      ) : canPrompt || prompting ? (
        <button
          data-testid="pwa-install-button" type="button" onClick={triggerPrompt} disabled={prompting}
          className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-[0.15em] bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors"
        >
          {prompting ? <CircleNotch size={18} className="animate-spin" weight="bold" /> : <DownloadSimple size={18} weight="bold" />}
          Install App
        </button>
      ) : noPromptPhase === "fallback" ? (
        <div data-testid="pwa-install-fallback">
          <p className="mt-4 t-support text-muted-foreground text-left">
            Install the app from Chrome&apos;s own menu to finish:
          </p>
          <ol className="mt-4 space-y-3">
            <Step n={1}>
              Open the Chrome menu <DotsThreeVertical size={15} weight="bold" className="inline text-primary" /> at
              the top right of this screen.
            </Step>
            <Step n={2}>
              Tap <b>Install app</b> or <b>Add to Home screen</b>.
            </Step>
            <Step n={3}>
              Confirm the installation.
            </Step>
            <Step n={4}>
              Open <b>Rotary GOV Report Builder</b> using the resulting icon on your home screen.
            </Step>
          </ol>
          <p className="mt-4 t-support text-muted-foreground text-left">
            This screen will confirm automatically once the app is installed. Your device
            pairing and Rotary Access PIN remain active throughout.
          </p>
          {!diagnostics ? (
            <button
              data-testid="install-help-toggle" type="button" onClick={showHelp}
              className="mt-4 text-sm text-primary hover:underline"
            >
              Installation help
            </button>
          ) : (
            <div data-testid="install-diagnostics" className="mt-4 text-left">
              <p className="t-support text-muted-foreground">
                If neither option appears in Chrome&apos;s menu, share these local diagnostics
                with your district administrator. Nothing is sent automatically.
              </p>
              <pre className="mt-2 p-3 bg-background border border-border rounded-md text-[11px] leading-relaxed whitespace-pre-wrap break-words select-all">{diagnostics}</pre>
              <button
                data-testid="copy-diagnostics-button" type="button" onClick={copyDiagnostics}
                className="mt-2 inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <CopySimple size={15} weight="bold" /> {copied ? "Copied" : "Copy diagnostics"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <p data-testid="pwa-install-waiting" className="mt-6 t-support text-muted-foreground flex items-center justify-center gap-2">
          <CircleNotch size={16} className="animate-spin text-primary" weight="bold" /> Preparing installation…
        </p>
      )}

      <p className="mt-6 t-support text-muted-foreground text-left flex items-start gap-2">
        <ShieldCheck size={18} weight="duotone" className="text-primary shrink-0 mt-0.5" />
        <span>
          The Rotary GOV Report Builder opens only on this paired phone, protected by your
          Rotary Access PIN.
        </span>
      </p>
    </div>
  );
}
